import { Link, Route, Router, Switch, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import {
  BookMarked,
  ClipboardList,
  ExternalLink,
  HelpCircle,
  Moon,
  Search,
  Sun,
} from "lucide-react";
import { useEffect, useState } from "react";

import { SearchPage } from "./pages/SearchPage";
import { ResultsPage } from "./pages/ResultsPage";
import { ComparisonPage } from "./pages/ComparisonPage";
import { SavedPanelsPage } from "./pages/SavedPanelsPage";
import { DecisionLogPage } from "./pages/DecisionLogPage";
import { WelcomePage } from "./pages/WelcomePage";
import { GuidePage } from "./pages/GuidePage";
import { cn } from "./lib/utils";

const WELCOME_SEEN_KEY = "academ.welcome.seen";

function ThemeToggle() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("academ.theme", isDark ? "dark" : "light");
  }, [isDark]);
  return (
    <button
      type="button"
      onClick={() => setIsDark((v) => !v)}
      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-foreground transition-colors hover:bg-muted"
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

function BrandMark() {
  return (
    <Link to="/" className="flex items-center gap-2">
      <span
        className="grid h-7 w-7 place-items-center rounded-full text-[10px] font-bold text-white shadow-sm"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, hsl(186 84% 50%), hsl(213 60% 26%))",
        }}
        aria-hidden
      >
        AC
      </span>
      <div className="leading-tight">
        <div className="font-display text-sm font-semibold tracking-tight">
          ACA<span className="text-accent">DEM</span>
        </div>
      </div>
    </Link>
  );
}

const NAV_TABS: Array<{ to: string; label: string; icon: typeof Search }> = [
  { to: "/", label: "Query", icon: Search },
  { to: "/panels", label: "Saved Panels", icon: BookMarked },
  { to: "/decisions", label: "Decision Log", icon: ClipboardList },
];

function NavTab({ tab }: { tab: (typeof NAV_TABS)[number] }) {
  const [location] = useLocation();
  const Icon = tab.icon;
  const active =
    tab.to === "/"
      ? location === "/" ||
        location.startsWith("/results") ||
        location.startsWith("/comparison")
      : location === tab.to || location.startsWith(tab.to);
  return (
    <Link
      to={tab.to}
      className={cn(
        "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-accent/10 text-accent-foreground ring-1 ring-accent/30"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
      {tab.label}
    </Link>
  );
}

function HeaderExternalLinks() {
  return (
    <div className="hidden items-center gap-4 text-xs text-muted-foreground sm:flex">
      <a
        href="https://alphafold.ebi.ac.uk/"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        AlphaFold DB
        <ExternalLink className="h-3 w-3" />
      </a>
      <a
        href="https://string-db.org/"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        STRING
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}

function HeaderGuideLink() {
  const [location] = useLocation();
  const active = location === "/guide" || location === "/welcome";
  return (
    <Link
      to="/guide"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "border-accent/40 bg-accent/10 text-accent-foreground"
          : "border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
      aria-label="Open the quick guide"
      title="Quick guide & welcome"
    >
      <HelpCircle className="h-3.5 w-3.5" />
      Guide
    </Link>
  );
}

function FirstVisitRedirect() {
  const [location, setLocation] = useLocation();
  useEffect(() => {
    if (location !== "/") return;
    try {
      const seen = window.localStorage.getItem(WELCOME_SEEN_KEY);
      if (seen) return;
      window.localStorage.setItem(WELCOME_SEEN_KEY, "1");
      setLocation("/welcome");
    } catch {
      // Privacy-mode / sandboxed storage — fall through silently and let the
      // user land on the Query page as if they had already seen welcome.
    }
  }, [location, setLocation]);
  return null;
}

function AppChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-20 border-b border-border/80 bg-background/85 backdrop-blur">
        <div className="container flex h-14 items-center gap-4">
          <BrandMark />
          <nav className="flex items-center gap-1">
            {NAV_TABS.map((tab) => (
              <NavTab key={tab.to} tab={tab} />
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3 sm:gap-4">
            <HeaderExternalLinks />
            <HeaderGuideLink />
            <ThemeToggle />
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-border/80 py-4 text-xs text-muted-foreground">
        <div className="container flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl space-y-1.5">
            <p>
              Predictions are hypotheses. Every interaction is a candidate, not a fact. Validate before
              concluding.
            </p>
            <p>
              Developed and prepared by Stepan Perepechenov. If you have any problems, just ask me -{" "}
              <a
                href="mailto:ssperepechenov@gmail.com"
                className="text-foreground underline decoration-muted-foreground/60 underline-offset-2 hover:text-accent hover:decoration-accent"
              >
                ssperepechenov@gmail.com
              </a>
            </p>
          </div>
          <span className="shrink-0 font-mono">
            app {import.meta.env.VITE_APP_VERSION ?? "0.9.0-alpha"} &middot; kb{" "}
            {import.meta.env.VITE_KB_VERSION ?? "1.0.0"}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <Router hook={useHashLocation}>
      <FirstVisitRedirect />
      <AppChrome>
        <Switch>
          <Route path="/" component={SearchPage} />
          <Route path="/welcome" component={WelcomePage} />
          <Route path="/guide" component={GuidePage} />
          <Route path="/results" component={ResultsPage} />
          <Route path="/comparison" component={ComparisonPage} />
          <Route path="/panels" component={SavedPanelsPage} />
          <Route path="/decisions" component={DecisionLogPage} />
          <Route>{() => <div className="container py-12 text-muted-foreground">Not found.</div>}</Route>
        </Switch>
      </AppChrome>
    </Router>
  );
}
